/**
 * 「拍摄任务书」横幅(2026-09-12 目的锁定 A2;2026-09-13 A2 再瘦身)—— 目的锁定后目的在页面上的唯一呈现。
 *
 * ═══ 为什么是横幅、为什么不能换目的 ══════════════════════════════════════════════
 * 老板拍板:「进去之后不能切换目的,一开始给 AI 导演的东西更精准」。目的在 hub 的 8 张
 * 卡上选定,进创建页就锁死。所以这条横幅**只展示、不给换**:
 *   - 原来的「视频目的」来源条(SourceStrip) + 备料台目的行的「换目的 / 换成 X」链接一并删除;
 *   - 页内换叙事弧 = 换目的(带货/硬广现在是 hub 上两张独立卡),违背锁定,也删。
 * 选错了唯一的出口是「回视频舱重选」—— 已填内容由 videoIntakeStash 暂存,回去不白填。
 *
 * ═══ ★2026-09-13:拍法选择器 + 叙事弧节拍带都搬走了 ════════════════════════════
 * 横幅现在**只留** 目的药丸 + 副题 + 「回视频舱重选」。原来挂在这里的两样都下移进了
 * 「时长×拍法 联动卡」(`VideoShootPlanCard`):
 *   - 拍法「换一张」(`VideoFormulaPicker` compact)—— 它与时长/节拍/合身度联动,理应同处一卡;
 *   - 叙事弧节拍带 —— 改成读**当前拍法的 beats**(没有拍法才回落目的 `arc`),不再是横幅里写死四段。
 * 横幅回归它的本分:一眼看清「这条片是干什么的、锁了、要换回舱里选」。
 */
import { ArrowRight } from "lucide-react";

import type { BriefPurposeDef } from "@/entities/scene-card/model/briefPurpose";
import { cn } from "@/shared/lib/utils";

interface Props {
  purpose: BriefPurposeDef;
  /** 回视频舱重选(内容已由调用方暂存)。 */
  onBackToHub: () => void;
  /** 编排/出图进行中:整条只读。 */
  disabled?: boolean;
}

export function VideoPurposeBanner({ purpose, onBackToHub, disabled }: Props) {
  return (
    <section
      data-video-purpose-banner
      className={cn(
        "rounded-[calc(var(--radius)*1.15)] border-[1.5px] border-primary/40 bg-card/60 px-5 py-4",
        disabled && "pointer-events-none opacity-60"
      )}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="shrink-0 text-2xs font-medium text-muted-foreground">拍摄任务书</span>
        <span
          data-testid="purpose-banner-pill"
          className="shrink-0 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground"
        >
          {purpose.label}
        </span>
        <span className="min-w-0 text-2xs text-muted-foreground">{purpose.usage}</span>
        {/* 「选错目的?回视频舱重选(已填内容会保留)」—— 换目的的唯一合法出口 */}
        <button
          type="button"
          disabled={disabled}
          onClick={onBackToHub}
          data-testid="purpose-back-to-hub"
          className="ml-auto inline-flex shrink-0 cursor-pointer items-center gap-0.5 rounded text-2xs text-muted-foreground underline decoration-dashed underline-offset-2 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-50"
        >
          选错目的?回视频舱重选(已填内容会保留)
          <ArrowRight className="h-3 w-3" />
        </button>
      </div>
    </section>
  );
}
